import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { Hourglass, AlertCircle, ArrowRight } from "lucide-react";
import {
  extendCompanyTrial,
  type CompanySubscriptionView,
  coerceTier,
} from "../../../api/General/Subscription";
import { addDays, formatDate } from "./helpers";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanySubscriptionView | null;
  onSuccess: () => void;
}

export default function ExtendTrialDialog({ open, onOpenChange, company, onSuccess }: Props) {
  const { t } = useTranslation();
  const [days, setDays] = useState<number>(30);
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDays(30);
      setNotes("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  // Computed new trial-end based on the *current* trialEndsAt (or "now" if past).
  const projectedEnd = useMemo(() => {
    if (!company || days <= 0) return null;
    const base = company.trialEndsAt ? new Date(company.trialEndsAt) : new Date();
    const baseForward = base.getTime() < Date.now() ? new Date() : base;
    return addDays(baseForward, days);
  }, [company, days]);

  const submit = async () => {
    if (!company) return;
    if (!Number.isFinite(days) || days <= 0) {
      setError(t("Additional Days"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await extendCompanyTrial(company.id, {
        additionalDays: days,
        notes: notes.trim() || null,
      });
      if (result.errors && result.errors.length > 0) {
        setError(result.errors[0]?.message || t("Failed to update subscription"));
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch (e) {
      const message =
        (e as { response?: { data?: { errors?: { message?: string }[] } } })?.response?.data
          ?.errors?.[0]?.message || t("Failed to update subscription");
      setError(message);
      setSubmitting(false);
    }
  };

  if (!company) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex items-center justify-center">
              <Hourglass size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle>{t("Extend Trial")}</DialogTitle>
              <DialogDescription className="mt-1">
                {company.name}
                <span className="mx-1.5 text-muted-foreground/50">·</span>
                <span className="text-xs uppercase tracking-wider">{t(coerceTier(company.tier))}</span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* Current → projected new end (mini-diff) */}
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <div className="flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("Current Trial End")}
                </div>
                <div className="mt-0.5 font-medium tabular-nums text-foreground">
                  {formatDate(company.trialEndsAt)}
                </div>
              </div>
              <ArrowRight size={16} className="text-muted-foreground shrink-0 rtl:rotate-180" />
              <div className="min-w-0 text-end">
                <div className="text-xs uppercase tracking-wider text-amber-700 dark:text-amber-400">
                  {t("New Trial End")}
                </div>
                <div className="mt-0.5 font-medium tabular-nums text-amber-700 dark:text-amber-300">
                  {projectedEnd ? formatDate(projectedEnd.toISOString()) : "—"}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="et-days">
              {t("Additional Days")}
            </label>
            <Input
              id="et-days"
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDays(Math.max(1, parseInt(e.target.value || "0", 10) || 0))
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="et-notes">
              {t("Notes (optional)")}
            </label>
            <textarea
              id="et-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("Add a note about this change")}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors disabled:opacity-50"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} loading={submitting} variant="warning">
            {t("Extend Trial")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
